import { useState } from 'react';
import { useTranslation } from 'next-i18next';

import * as Dialog from '@radix-ui/react-dialog';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { cx } from 'class-variance-authority';
import Barcode from 'react-barcode';

import { Voucher } from '@oresundsbron/api';
import { Box, Button, Card, Typography } from '@oresundsbron/bridge-ui';
import { OpenLinkInNew, Print, PrintOutline } from '@oresundsbron/icons';

import { RichText } from '~/components/RichText';
import { useLocale } from '~/stores/locale';

type AquireProps = {
  title: string;
  open: boolean;
  eanCode?: string | null;
  urlToDiscount?: string | null;
  onChange: (open: boolean) => void;
  customerNo?: string;
} & Voucher;

export const Aquire = ({
  open,
  urlToDiscount,
  title,
  description,
  eanCode,
  terms,
  validUntil,
  customerNo,
  onChange,
}: AquireProps) => {
  const { dateLocale } = useLocale();
  const { t } = useTranslation(['club', 'common'], { nsMode: 'fallback' });

  return (
    <Dialog.Root open={open} onOpenChange={onChange}>
      <Dialog.Trigger />
      <Dialog.Portal className="">
        <Dialog.Overlay className="fixed inset-0 z-20 bg-black/25" />

        <Card.Root
          as={Dialog.Content}
          className={cx([
            'fixed',
            'inset-1/2',
            'z-20',
            'h-full',
            'w-full',
            'max-w-xl',
            'translate-x-[-50%]',
            'translate-y-[-50%]',
            'rounded-none',
            'sm:h-[85%]',
            'sm:rounded-xl',
            'print:absolute',
            'print:inset-0',
            'print:h-auto',
            'print:w-full',
            'print:max-w-none',
            'print:translate-x-0',
            'print:translate-y-0',
          ])}
          data-mode="club"
        >
          <Card.Header className="relative flex gap-2 bg-primary-700">
            <Typography
              as="h1"
              intent="headline"
              color="white"
              className="!mb-0 !text-xl font-semibold"
            >
              {title}
            </Typography>
          </Card.Header>
          <ScrollArea.Root
            className={cx([
              'relative',
              'grid',
              'h-full',
              'max-h-full',
              'grid-rows-[1fr_11rem]',
              'overflow-hidden',
              'print:overflow-visible',
            ])}
          >
            <Card.Content
              as={ScrollArea.Viewport}
              className="!block h-full max-h-[85vh] !p-0 md:max-h-[70vh] print:max-h-max "
            >
              <div className="p-6">
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-2">
                    <Typography
                      as="h2"
                      intent="headline"
                      className="col-span-full !mb-0 text-2xl !leading-8 text-type-heading sm:!text-2xl"
                    >
                      {eanCode
                        ? t('club.voucher.aquire.eanSubtitle')
                        : t('club.voucher.aquire.urlSubtitle')}
                    </Typography>

                    {customerNo ? (
                      <div className="flex text-type-secondary">
                        <span className="font-semibold">
                          {t('club.voucher.aquire.customerNoText')}&nbsp;
                        </span>
                        <span className="font-normal">{customerNo}</span>
                      </div>
                    ) : null}

                    {description ? (
                      <Box className="col-span-full text-type-secondary">
                        <RichText content={description} />
                      </Box>
                    ) : null}
                  </div>

                  {urlToDiscount ? (
                    <Box className="col-span-full flex print:hidden">
                      <Button
                        as="a"
                        endIcon={<OpenLinkInNew />}
                        href={urlToDiscount}
                        style={{ textDecoration: 'none' }}
                        className={cx([
                          'rounded-full',
                          'border-2',
                          'border-primary-700',
                          'bg-white',
                          '!text-primary-700',
                          'pr-6',
                          'hover:bg-primary-700',
                          'hover:!text-white',
                          'focus:!border-black',
                          'focus:bg-primary-700',
                          'focus:!text-white',
                          'focus:!shadow-button-focus',
                          'focus:ring-2',
                          'focus:ring-white',
                          'active:bg-primary-700',
                          'active:!text-white',
                          'active:border-primary-700',
                        ])}
                      >
                        {t('club.voucher.aquire.urlAquireButton')}
                      </Button>
                    </Box>
                  ) : null}
                  {eanCode ? (
                    <div className="flex flex-col gap-2 rounded-xl border-2 bg-neutral-50 p-3">
                      <Box className="flex justify-around">
                        <Barcode
                          value={eanCode}
                          width={2}
                          format={eanCode.length === 13 ? 'EAN13' : undefined}
                          background="transparent"
                        />
                      </Box>
                      <Box className="flex justify-around print:hidden">
                        <Button
                          intent="outlined"
                          startIcon={<PrintOutline />}
                          className={cx([
                            'rounded-full',
                            'border-2',
                            'border-primary-700',
                            'bg-white',
                            '!text-primary-700',
                            'pr-6',
                            'hover:bg-primary-700',
                            'hover:!text-white',
                            'focus:!border-black',
                            'focus:bg-primary-700',
                            'focus:!text-white',
                            'focus:!shadow-button-focus',
                            'focus:ring-2',
                            'focus:ring-white',
                            'active:bg-primary-700',
                            'active:!text-white',
                            'active:border-primary-700',
                          ])}
                          onClick={() => window.print()}
                        >
                          <div className="flex">
                            {t('club.voucher.aquire.eanPrint')}
                          </div>
                        </Button>
                      </Box>
                    </div>
                  ) : null}
                </div>
              </div>

              {terms ? (
                <div className="my-4 border-t-2 border-neutral-200 bg-neutral-50 p-6">
                  <Typography
                    as="h4"
                    intent="title"
                    className="col-span-full text-lg font-semibold"
                  >
                    {t('club.voucher.aquire.termsHeading')}
                  </Typography>
                  {terms && (
                    <>
                      <Box className="col-span-full">
                        <RichText content={terms} />
                      </Box>
                    </>
                  )}
                  <Typography
                    intent="label"
                    size="lg"
                    weight="normal"
                    className="col-span-full mt-4 italic"
                  >
                    {t('club.voucher.aquire.validUntilText', {
                      date: new Date(validUntil).toLocaleDateString(
                        dateLocale?.code || 'sv-SE',
                        {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        }
                      ),
                    })}
                  </Typography>
                </div>
              ) : null}
            </Card.Content>

            <Card.Footer className="flex justify-end border-t-2 border-neutral-200 ">
              <Dialog.Close asChild>
                <Button
                  className={cx([
                    'ml-auto',
                    'mr-6',
                    'rounded-full',
                    '',
                    'border-2',
                    'border-white',
                    'bg-primary-700',
                    'p-6',
                    'hover:bg-white',
                    'hover:!text-black',
                    'focus:!border-black',
                    'focus:bg-white',
                    'focus:text-black',
                    'focus:!shadow-button-focus',
                    'focus:ring-2',
                    '',
                    'focus:ring-white',
                    'active:bg-white',
                    'active:!text-black',
                  ])}
                >
                  {t('action.close')}
                </Button>
              </Dialog.Close>
            </Card.Footer>
            <ScrollArea.Scrollbar
              orientation="vertical"
              className="flex w-2.5 touch-none select-none bg-transparent p-0.5"
            >
              <ScrollArea.Thumb className="relative flex-1 rounded-lg bg-neutral-500 before:absolute before:left-1/2 before:top-1/2 before:h-full before:min-h-[44px] before:w-full before:min-w-[44px] before:translate-x-1/2 before:translate-y-1/2 before:content-none" />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>
        </Card.Root>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
